// English translations for M'art website
export const en = {
  // Navigation
  nav: {
    home: "Home",
    method: "The Method",
    diy: "Make It Yourself",
    product: "Ready-Made",
    community: "Community",
    whyOpen: "Open Code",
    downloadPdf: "Technical Info"
  },

  // Home page
  home: {
    hero: {
      title: "Natural Deodorant. Knowledge First.",
      subtitle: "This is an open formula, free for anyone who wants to make it themselves, or simply buy it ready-made. Pure ingredients, full transparency.",
      fullGuide: "Full guide",
      notCorporate: "Independent brand",
      freeKnowledge: "Open knowledge"
    },
    paths: {
      title: "How would you like to start?",
      diy: {
        title: "Make It Yourself",
        subtitle: "Free",
        description: "Full access to formula, ingredients list and production instructions"
      },
      buy: {
        title: "Buy Ready-Made",
        subtitle: "₪50",
        description: "Exact same product, just made for you. 50ml."
      },
      community: {
        title: "See Experiments",
        subtitle: "Community",
        description: "Experiments from others, tips and shared knowledge"
      }
    }
  },

  // Method page
  method: {
    title: "The Method",
    subtitle: "The philosophy and logic behind the formula",
    sections: {
      whyNatural: {
        title: "Why natural deodorant?",
        content: "Commercial deodorants contain aluminum, parabens and chemicals that are absorbed through the skin. Your body knows how to regulate itself, all it needs is support, not interference."
      },
      whyFormulation: {
        title: "Why does this formula work?",
        content: "The combination of baking soda (pH neutralization), corn starch (moisture absorption) and natural oils (antibacterial) provides a complete solution for body odor, without blocking pores."
      },
      whyOpen: {
        title: "Why is openness important?",
        content: "When you know what's inside, you can choose. You can adapt. You can improve. Transparency empowers communities."
      },
      whyAI: {
        title: "Why was AI used as a tool?",
        content: "AI helped analyze hundreds of formulas, identify optimal ratios between ingredients and adapt the formula to different climate conditions. The tool is ours, the result is yours."
      }
    }
  },

  // DIY page
  diy: {
    title: "Make It Yourself",
    subtitle: "Full access to the formula, free, no registration required",
    
    overview: {
      title: "What's inside?",
      description: "The formula is based on four building blocks: fat base, active powders, stabilizers and soothers, and natural fragrance."
    },

    ingredients: {
      title: "Ingredients List (100 grams)",
      items: [
        {
          name: "Coconut Oil",
          amount: "2.5 tbsp / 7.5 tsp",
          volume: "37.5g",
          percent: "37.5%",
          role: "Main fat base and carrier for other ingredients",
          action: "Natural antibacterial, helps neutralize odor-causing bacteria",
          texture: "Melts on skin contact, creates a creamy, smooth feel"
        },
        {
          name: "Shea Butter",
          amount: "1.3 tbsp / 4 tsp",
          volume: "20g",
          percent: "20%",
          role: "Softener and stabilizer",
          action: "Rich in vitamins A and E, soothes irritated skin",
          texture: "Adds body and creamy consistency"
        },
        {
          name: "Corn Starch",
          amount: "1.1 tbsp / 3.5 tsp",
          volume: "17.5g",
          percent: "17.5%",
          role: "Absorbs moisture and odor",
          action: "Absorbs light sweat, keeps skin dry",
          texture: "Gives a velvety, smooth, non-shiny look"
        },
        {
          name: "Sodium Bicarbonate",
          quality: "Cosmetic-grade baking soda",
          amount: "1 tbsp / 3 tsp",
          volume: "15g",
          percent: "15%",
          role: "Active ingredient for odor neutralization",
          action: "Balances pH and prevents bacterial growth",
          note: "Allowed range: 12%-18%, reduced dosage to prevent irritation"
        },
        {
          name: "Beeswax",
          amount: "0.3 tbsp / 1 tsp",
          volume: "5g",
          percent: "5%",
          role: "Natural stabilizer",
          action: "Maintains mixture stability and prevents separation",
          texture: "Softer texture, reduces rigidity and eases application"
        },
        {
          name: "Aloe Vera Gel",
          amount: "0.3 tbsp / 0.9 tsp",
          volume: "4.5g",
          percent: "4.5%",
          role: "Softening, cooling and moisturizing",
          action: "Soothes skin and balances baking soda effect",
          texture: "Contributes to freshness"
        },
        {
          name: "Essential Oils (lavender or choice)",
          amount: "~10 drops",
          volume: "0.5g",
          percent: "0.5%",
          role: "Fragrance and natural disinfection",
          action: "Antibacterial and refreshing"
        }
      ]
    },

    proportions: {
      title: "Formulation Ratio (percentages, per 100g)",
      items: [
        { name: "Coconut Oil", percent: "37.5%" },
        { name: "Shea Butter", percent: "20%" },
        { name: "Corn Starch", percent: "17.5%" },
        { name: "Baking Soda (cosmetic)", percent: "15%" },
        { name: "Beeswax", percent: "5%" },
        { name: "Aloe Vera Gel", percent: "4.5%" },
        { name: "Essential Oils", percent: "0.5%" }
      ],
      note: "Total 100%, Percentages may vary slightly depending on coconut oil consistency"
    },

    stabilizationTip: {
      title: "Formula Stabilization Tip (Preventing Separation)",
      content: "Aloe vera gel contains a water phase that doesn't naturally mix with oils and butters, which can lead to product separation or mold formation without a proper preservative.",
      alternative: "Prefer to make deodorant without separation risk? You can completely skip the aloe vera gel (4.5%) and add that weight directly to the shea butter (raising shea butter from 20% to 24.5%). This will give the product full long-term stability, a rich smooth texture, and eliminate the need for a preservative.",
      castorOil: "Alternatively, you can use Castor Oil as a substitute for aloe vera gel, castor oil blends well with oils and fats and provides natural stabilization without separation risk."
    },

    process: {
      title: "Technical Loading Order, Production Process",
      steps: [
        {
          number: 1,
          title: "Melting Stage (Bain-Marie)",
          content: "In a heat-resistant glass bowl, melt together coconut oil, shea butter and beeswax. Heat in bain-marie until fully and evenly melted.",
          warning: "Recommended temperature: up to 40°C, no more, to preserve ingredient quality."
        },
        {
          number: 2,
          title: "Cooling Stage",
          content: "Remove from heat and wait 1-2 minutes. Add aloe vera gel and mix well until smooth."
        },
        {
          number: 3,
          title: "Powder Stage",
          content: "In a separate bowl, mix baking soda and corn starch well. Gradually add to the warm mixture while stirring continuously until fully homogeneous and lump-free."
        },
        {
          number: 4,
          title: "Final Stage, Essential Oils",
          content: "When mixture is warm but not hot: add essential oils and mix well for even distribution."
        },
        {
          number: 5,
          title: "Pouring and Setting",
          content: "Transfer to a clean glass jar. Let set at room temperature for 3-4 hours."
        }
      ]
    },

    usage: {
      title: "Usage Instructions",
      instructions: [
        "Take a small amount of deodorant with a clean spoon",
        "Apply a thin layer on clean, dry underarm skin",
        "Allow a few seconds to absorb before dressing"
      ],
      frequency: "Frequency: 1-2 times daily, as needed",
      cleaning: "Spoon cleaning: After each use, wipe the spoon with dry paper",
      storage: "Storage: Keep in a cool, dry place, away from direct heat"
    },

    tips: {
      title: "Tip",
      content: "If the product hardens due to cold weather or changes texture, add a little aloe vera gel or coconut oil, mix well and restore desired texture."
    },

    sensitivity: {
      title: "Sensitivity Notes",
      content: "This is shared experience, not medical advice. Each user is responsible for their own skin. No guarantees, no promises, just full transparency."
    }
  },

  // Product page
  product: {
    title: "Ready-Made Deodorant",
    subtitle: "Exact same process, just made for you",
    description: "If you don't have time or prefer not to make it yourself, you can simply buy. Exact same product, same ingredients, same process.",
    price: "₪50",
    volume: "50ml",
    includes: [
      "Amber glass jar",
      "Wooden spoon",
      "Usage instructions"
    ],
    cta: "Order via WhatsApp",
    note: "Purchase is optional. All information is available free on the \"Make It Yourself\" page.",
    whatsappMessage: "Hi M'art, I'd like to order natural deodorant (50ml, ₪50)"
  },

  // Community page
  community: {
    title: "Community",
    subtitle: "A shared lab, not a social network",
    description: "Share experiments, results and insights. This isn't a place for advertising, it's a shared lab notebook.",
    loginPrompt: "Sign in with Google to share",
    loginButton: "Sign in with Google",
    logoutButton: "Sign out",
    newPost: {
      placeholder: "Share your experiment...",
      addImages: "Add images (up to 3)",
      submit: "Post"
    },
    comments: {
      title: "Comments",
      placeholder: "Write a comment...",
      submit: "Send"
    },
    empty: "No posts yet. Be the first to share!"
  },

  // Why Open page
  whyOpen: {
    title: "Why Open?",
    subtitle: "A manifesto for transparent cosmetics",
    sections: {
      corporations: {
        title: "The reason to DIY",
        content: "Large cosmetics companies sell you mostly a story, branding, packaging, celebrity endorsements. The ingredients? Usually simple and inexpensive. The difference between production cost and retail price is mainly marketing budget. We believe in a different way."
      },
      free: {
        title: "Why is the formula free?",
        content: "Because knowledge should be accessible. If someone wants to make it themselves, they should. If someone prefers to buy ready-made, that's an option too. No manipulation, no hiding."
      },
      feedback: {
        title: "Why is feedback important?",
        content: "We're not perfect. If you have an idea for improvement, change, adaptation, we'd love to hear. This formula is a starting point, not an end point."
      },
      testing: {
        title: "Are ideas tested?",
        content: "Good feedback and interesting experiments may be tested in practice after consultation with AI, in full transparency. If something works better, we'll adopt it."
      }
    },
    statement: "This site was built so you can know, choose and make it yourself. Purchase is an option, not a goal."
  },

  // Footer
  footer: {
    disclaimer: "This is shared experience, not medical advice. Each user is responsible for their own skin.",
    copyright: "© M'art, Natural Deodorant, Open Source"
  },

  // Common
  common: {
    loading: "Loading...",
    error: "Error",
    back: "Back",
    close: "Close"
  }
};

export default en;
